"use client"
import { createContext, useContext, useState } from "react"
import { cn } from "../../lib/utils"

// Create a context to manage the active tab state
const TabsContext = createContext(null)

export function Tabs({ className, value: controlledValue, defaultValue, onValueChange: controlledOnValueChange, children, ...props }) {
  // Use state if this is an uncontrolled component
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue || "")

  // Determine if this is controlled or uncontrolled
  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : uncontrolledValue

  const onValueChange = (newValue) => {
    // Update internal state if uncontrolled
    if (!isControlled) {
      setUncontrolledValue(newValue)
    }
    // Call external handler if provided
    if (controlledOnValueChange) {
      controlledOnValueChange(newValue)
    }
  }

  return (
    <TabsContext.Provider value={{ value, onValueChange }}>
      <div className={cn("", className)} {...props}>
        {children}
      </div>
    </TabsContext.Provider>
  )
}

export function TabsList({ className, ...props }) {
  return (
    <div
      className={cn(
        "inline-flex h-10 items-center rounded-md bg-muted p-1 text-muted-foreground",
        className,
      )}
      {...props}
    />
  )
}

export function TabsTrigger({ className, value, activeValue: externalActiveValue, onValueChange: externalOnValueChange, ...props }) {
  // Use context for state management
  const context = useContext(TabsContext)

  // Use context values if available, or fall back to direct props for backward compatibility
  const activeValue = context?.value !== undefined ? context.value : externalActiveValue
  const onValueChange = context?.onValueChange || externalOnValueChange

  const isActive = value === activeValue

  return (
    <button
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5  font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        isActive ? "bg-background text-foreground shadow-md" : "hover:bg-muted hover:text-foreground",
        className,
      )}
      onClick={() => onValueChange && onValueChange(value)}
      data-state={isActive ? "active" : "inactive"}
      {...props}
    />
  )
}

export function TabsContent({ className, value, activeValue: externalActiveValue, ...props }) {
  // Use context for state management
  const context = useContext(TabsContext)

  // Use context values if available, or fall back to direct props for backward compatibility
  const activeValue = context?.value !== undefined ? context.value : externalActiveValue

  const isActive = value === activeValue

  if (!isActive) return null

  return (
    <div
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
      data-state={isActive ? "active" : "inactive"}
      {...props}
    />
  )
}
